import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme } from '@mui/material/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import api from './api';
import qs from 'qs';
import VerificationCodeDialog from './VerificationCodeDialog';
import { useUser } from './UserContext';
import BusyOverlay from './BusyOverlay';
import GoogleSignInButton from './GoogleSignInButton';
import AppleSignInButton from './AppleSignInButton';
import { INVITE_CODE_KEY } from './JoinClub';

const SignUpPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [recaptchaValue, setRecaptchaValue] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    repeatPassword: ''
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [emailCode, setEmailCode] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const data = {
      userEmail: form.email,
      userPassword: form.password,
      userFullName: form.fullName,
      captchaToken: recaptchaValue
    };

    try {
      setIsLoading(true);
      const res = await api.post('/signup', qs.stringify(data), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      setIsLoading(false);
      console.log('Success:', res.data);

      if (res.status === 206) {
        setShowCodeModal(true);
        return;
      } else {
        setError(res.statusText || 'Unexpected Response');
      }
    } catch (err) {
      setIsLoading(false);
      if (err.response?.status === 409) {
        setError('User already exists');
      } else {
        setError(err.response?.data || 'Signup failed');
      }
      console.error('Signup error:', err.response?.data || err.message);
    }
  };

  const handleVerifySuccess = () => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    setShowCodeModal(false);
    const pendingInvite = sessionStorage.getItem(INVITE_CODE_KEY);
    navigate(pendingInvite ? `/join?inviteCode=${pendingInvite}` : '/dashboard');
  };

  const isFormValid = form.fullName &&
                     form.email &&
                     form.password &&
                     form.repeatPassword &&
                     (form.password === form.repeatPassword) &&
                     recaptchaValue &&
                     acceptedTerms;

  return (
    <>
      <BusyOverlay open={isLoading} message="Signing you up..." />
      <Box sx={{
        marginBottom: 8,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          opacity: 0.1,
          pointerEvents: 'none',
          zIndex: 0
        }}>
          <img src="/mascot.png" alt="" style={{ width: '100%' }} />
        </div>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '90%',
            textAlign: 'center',
            backgroundColor: theme.palette.background,
            color: theme.palette.text.primary
          }}
        >
          <Box sx={{ marginBottom: 3 }}>
            <Typography sx={{ fontWeight: 'bold', marginRight: 2 }}>SIGN UP</Typography>
            <Link
              to="/login"
              style={{
                textDecoration: 'none',
                marginRight: theme.spacing(2),
                color: theme.palette.text.primary,
              }}
            >
              LOGIN
            </Link>
          </Box>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <GoogleSignInButton label="Sign up with Google" />
          </Box>
          <Box sx={{ mt: 1 }}>
            <AppleSignInButton label="Sign up with Apple" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>or</Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
            }}
          >
            <TextField
              required
              label="Full Name"
              fullWidth
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              required
              label="Email"
              type="email"
              fullWidth
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              inputProps={{ maxLength: 254 }}
            />
            <TextField
              required
              label="Password"
              type="password"
              fullWidth
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              inputProps={{ maxLength: 1000 }}
            />
            <TextField
              required
              label="Repeat Password"
              type="password"
              fullWidth
              value={form.repeatPassword}
              onChange={(e) => setForm({ ...form, repeatPassword: e.target.value })}
              inputProps={{ maxLength: 1000 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ textAlign: 'left' }}>
                  I agree to the{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    style={{
                      color: theme.palette.link?.main || theme.palette.info.main,
                      textDecoration: 'underline',
                      fontWeight: 'bold'
                    }}
                  >
                    Terms of Use
                  </Link>, {' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    style={{
                      color: theme.palette.link?.main || theme.palette.info.main,
                      textDecoration: 'underline',
                      fontWeight: 'bold'
                    }}
                  >
                    Privacy Policy
                  </Link>, and {' '}
                  <Link
                    to="/acceptableuse"
                    target="_blank"
                    style={{
                      color: theme.palette.link?.main || theme.palette.info.main,
                      textDecoration: 'underline',
                      fontWeight: 'bold'
                    }}
                  >
                    Acceptable Use Policy
                  </Link>
                </Typography>
              }
              sx={{
                alignItems: 'flex-start',
                marginTop: 1,
                marginBottom: 1,
                '& .MuiFormControlLabel-label': {
                  marginTop: '2px'
                }
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                sitekey="6LefeGYrAAAAALRTOHKFlSdFzx9cSnpE6Nt52gcC"
                onChange={(value) => setRecaptchaValue(value)}
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid}
              style={{ color: theme.palette.primary.contrastText }}
            >
              Sign Up
            </Button>
          </form>
        </Paper>
        <VerificationCodeDialog
          open={showCodeModal}
          onClose={() => setShowCodeModal(false)}
          onVerifySuccess={handleVerifySuccess}
          email={form.email}
          code={emailCode}
          setCode={setEmailCode}
        />
      </Box>
    </>
  );
};

export default SignUpPage;